import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Filter, Search, Grid, Calendar, Users, GitBranch, ExternalLink, Globe } from 'lucide-react';
import { Component } from '../types';
import { filterComponents, sortComponents, getUniqueValues, getStatusColor, formatDate } from '../utils/filters';
import { SearchFilters, SortOption } from '../types';
import { useData } from '../contexts/DataContext';
import ComponentCard from '../components/ComponentCard';
import LoadingSpinner from '../components/LoadingSpinner';
import ComponentTypeIcon from '../components/ComponentTypeIcon';

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [filteredComponents, setFilteredComponents] = useState<Component[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({
    query: '',
    type: [],
    status: '',
    tags: [],
    cms: [],
    themes: [],
    accessibility: []
  });
  const [sortOption, setSortOption] = useState<SortOption>({
    field: 'name',
    direction: 'asc'
  });
  const [groupByType, setGroupByType] = useState(true);

  // Use global data store
  const { getProject, getProjectByName, getProjectComponents, getProjectComponentsByName, getAllProjects, isLoading, error } = useData();
  
  // Try to find project by ID first, then by name
  const project = projectId ? (getProject(projectId) || getProjectByName(projectId)) : undefined;
  const components = projectId ? (getProjectComponents(projectId) || getProjectComponentsByName(projectId)) : [];
  const allProjects = getAllProjects();

  // Get available types
  const availableTypes = useMemo(() => {
    const values = new Set<string>();
    const typeMapping = new Map<string, string[]>(); // Clean name -> original names
    
    components?.forEach(component => {
      const value = component.type;
      if (value) {
        // Remove numeric prefixes like "10-", "20-", etc.
        const cleanType = value.replace(/^\d+-/, '');
        values.add(cleanType);
        
        // Map clean name to original names for filtering
        if (!typeMapping.has(cleanType)) {
          typeMapping.set(cleanType, []);
        }
        typeMapping.get(cleanType)!.push(value);
      }
    });
    
    return { cleanTypes: Array.from(values).sort(), typeMapping };
  }, [components]);

  // Initialize filters with all available types
  useEffect(() => {
    if (availableTypes.cleanTypes.length > 0 && filters.type.length === 0) {
      // Initialize with all original type values
      const allOriginalTypes = availableTypes.cleanTypes.flatMap(cleanType => 
        availableTypes.typeMapping.get(cleanType) || []
      );
      setFilters(prev => ({ ...prev, type: allOriginalTypes }));
    }
  }, [availableTypes, filters.type.length]);

  // Apply filters and sorting
  useEffect(() => {
    if (components) {
      let filtered = filterComponents(components, {
        ...filters,
        query: searchQuery
      });
      filtered = sortComponents(filtered, sortOption);
      setFilteredComponents(filtered);
    }
  }, [components, filters, searchQuery, sortOption]);

  // Debug logging
  console.log('ProjectDetail render:', { 
    projectId, 
    windowLocation: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search
  });

  // Debug logging for data
  console.log('ProjectDetail data:', { 
    projectId,
    project, 
    isLoading, 
    error, 
    components,
    projectFoundById: projectId ? !!getProject(projectId) : false,
    projectFoundByName: projectId ? !!getProjectByName(projectId) : false
  });

  // Early return if no project ID
  if (!projectId) {
    console.error('ProjectDetail: No project name provided');
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Invalid Project</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            No project name provided in the URL.
          </p>
          <Link to="/projects" className="btn-primary">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Error Loading Project</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {error}
          </p>
          <Link to="/" className="btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // Show not found state
  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600 text-6xl mb-4">🔍</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Project Not Found</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            The project "{projectId}" could not be found.
          </p>
          {allProjects.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              No PatternLab projects were found. Please check your server configuration.
            </p>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Available projects: {allProjects.map(p => p.name).join(', ')}
            </p>
          )}
          <Link to="/" className="btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      query: '',
      type: availableTypes.cleanTypes.flatMap(cleanType => 
        availableTypes.typeMapping.get(cleanType) || []
      ),
      status: '',
      tags: [],
      cms: [],
      themes: [],
      accessibility: []
    });
    setSearchQuery('');
  };

  const availableStatuses = getUniqueValues(components, 'status');
  const availableTags = Array.from(new Set(components.flatMap(c => c.tags))).sort();

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <Link 
            to="/projects" 
            className="inline-flex items-center text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Projects
          </Link>
        </div>

        {/* Project Header */}
        <div className="card card-hover mb-8 p-8">
          <div>
              <h1 className="text-3xl font-bold gradient-text mb-2">
                {project.name}
              </h1>
              <p className="text-slate-600 dark:text-slate-300 mb-4">{project.description}</p>
              
              <div className="flex items-center space-x-6 text-sm text-slate-500 dark:text-slate-400">
                <div className="flex items-center space-x-1">
                  <Grid className="w-4 h-4" />
                  <span>{components.length} components</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>Updated {formatDate(project.lastUpdated)}</span>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mt-4 flex flex-wrap gap-2">
                {/* Git Repository */}
                {project.repository && (
                  <a
                    href={project.repository}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-glass text-sm flex items-center"
                  >
                    <GitBranch className="w-4 h-4 mr-2" />
                    <span>Git Repository</span>
                  </a>
                )}

                {/* Live PatternLab */}
                {project.patternlabUrl && (
                  <a
                    href={project.patternlabUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-glass-primary text-sm flex items-center"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    <span>Live PatternLab</span>
                  </a>
                )}
              </div>

              {/* Project metadata */}
              <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Project Details */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                    Project Details
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">PatternLab Version</span>
                      <span className="text-sm text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        {project.patternlabVersion || 'Not specified'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</span>
                      <span className={`badge ${getStatusColor(project.status || 'active')}`}>
                        {project.status || 'active'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">CMS</span>
                      {project.cms && project.cms.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {project.cms.map((cms, index) => (
                            <span key={index} className="text-xs bg-purple-600/80 text-white px-3 py-1 rounded-full font-medium backdrop-blur-sm">
                              {cms}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">None</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Themes</span>
                      {project.themes && project.themes.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {project.themes.map((theme, index) => (
                            <span key={index} className="text-xs bg-orange-600/80 text-white px-3 py-1 rounded-full font-medium backdrop-blur-sm">
                              {theme}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">None</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tech Stack */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    Tech Stack
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Templating</span>
                      <span className="text-sm text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        {project.techStack?.templating || 'Not specified'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">CSS</span>
                      <span className="text-sm text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        {project.techStack?.css || 'Not specified'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">JavaScript</span>
                      <span className="text-sm text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        {project.techStack?.js || 'Not specified'}
                      </span>
                    </div>
                    {project.techStack?.libraries && project.techStack.libraries.length > 0 ? (
                      <div>
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-2">Libraries</span>
                        <div className="flex flex-wrap gap-1">
                          {project.techStack.libraries.map((lib, index) => (
                            <span key={index} className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                              {lib}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Libraries</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">None</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Team */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></div>
                  Team ({(() => {
                    const owners = project.owner || [];
                    const maintainers = project.maintainers || [];
                    const uniqueMembers = new Set([...owners, ...maintainers]);
                    return uniqueMembers.size;
                  })()})
                </h3>
                <div className="text-gray-600 dark:text-gray-400">
                  {(() => {
                    const owners = project.owner || [];
                    const maintainers = project.maintainers || [];
                    
                    // Create a map to track unique people and their roles
                    const teamMembers = new Map();
                    
                    // Add owners
                    owners.forEach(person => {
                      teamMembers.set(person, ['owner']);
                    });
                    
                    // Add maintainers (merge roles if person is already in the map)
                    maintainers.forEach(person => {
                      if (teamMembers.has(person)) {
                        teamMembers.get(person).push('maintainer');
                      } else {
                        teamMembers.set(person, ['maintainer']);
                      }
                    });
                    
                    if (teamMembers.size > 0) {
                      return (
                        <div className="flex flex-wrap gap-2">
                          {Array.from(teamMembers.entries()).map(([person, roles], index) => (
                            <Link
                              key={index}
                              to={`/people/${encodeURIComponent(person)}`}
                              className="inline-flex items-center px-3 py-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 text-sm rounded-full hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors border border-indigo-200 dark:border-indigo-700"
                            >
                              <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-medium mr-2">
                                {person.charAt(0).toUpperCase()}
                              </div>
                              <span>{person}</span>
                              <span className="ml-1 text-xs opacity-75">({roles.join(', ')})</span>
                            </Link>
                          ))}
                        </div>
                      );
                    } else {
                      return (
                        <div className="text-gray-500 dark:text-gray-400 flex items-center space-x-2">
                          <Users className="w-4 h-4" />
                          <span>No team members specified</span>
                        </div>
                      );
                    }
                  })()}
                </div>
              </div>

              {/* Additional Project URLs */}
              {project.projectUrls && project.projectUrls.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
                    Additional URLs
                  </h3>
                  <div className="space-y-1">
                    {project.projectUrls.map((url, index) => (
                      <a
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-blue-600 dark:text-blue-400 hover:underline break-all hover:text-blue-800 dark:hover:text-blue-300 text-sm"
                      >
                        {url}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
        </div>

        {/* Filters and Search */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Components ({filteredComponents.length})
              </h2>
              
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 btn-secondary"
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </button>

              <button
                onClick={() => setGroupByType(!groupByType)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                  groupByType
                    ? 'bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 border border-primary-200 dark:border-primary-700'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600'
                }`}
              >
                <Grid className="w-4 h-4" />
                <span>{groupByType ? 'Grouped' : 'Ungrouped'}</span>
              </button>
            </div>

            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search components..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <select
                value={`${sortOption.field}-${sortOption.direction}`}
                onChange={(e) => {
                  const [field, direction] = e.target.value.split('-');
                  setSortOption({ field: field as any, direction: direction as any });
                }}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="name-asc">Name A-Z</option>
                <option value="name-desc">Name Z-A</option>
                <option value="lastUpdated-desc">Recently Updated</option>
                <option value="lastUpdated-asc">Oldest First</option>
                <option value="type-asc">Type A-Z</option>
                <option value="status-asc">Status A-Z</option>
              </select>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {availableTypes.cleanTypes.map(cleanType => {
                      const isActive = filters.type.some(originalType => 
                        availableTypes.typeMapping.get(cleanType)?.includes(originalType)
                      );
                      return (
                        <button
                          key={cleanType}
                          onClick={() => {
                            const originalTypes = availableTypes.typeMapping.get(cleanType) || [];
                            if (isActive) {
                              handleFilterChange('type', filters.type.filter(t => !originalTypes.includes(t)));
                            } else {
                              handleFilterChange('type', [...filters.type, ...originalTypes]);
                            }
                          }}
                          className={`px-3 py-2 text-sm font-bold rounded-lg transition-all duration-200 border flex items-center space-x-2 ${
                            isActive
                              ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900 border-gray-800 dark:border-gray-200 shadow-md hover:bg-gray-900 dark:hover:bg-gray-100 hover:border-gray-900 dark:hover:border-gray-100'
                              : 'bg-white dark:bg-gray-700 text-black dark:text-white border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500 shadow-sm'
                          } capitalize`}
                        >
                          <ComponentTypeIcon type={cleanType} size="sm" />
                          <span>{cleanType}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                  >
                    <option value="">All Statuses</option>
                    {availableStatuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                {/* Tags Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tags
                  </label>
                  <select
                    multiple
                    value={filters.tags}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      handleFilterChange('tags', selected);
                    }}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                  >
                    {availableTags.map(tag => (
                      <option key={tag} value={tag}>{tag}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  Clear all filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Components Display */}
        {groupByType ? (
          /* Components Grouped by Type */
          <div className="space-y-8">
            {(() => {
              // Group components by type
              const groupedComponents = filteredComponents.reduce((groups, component) => {
                const cleanType = component.type.replace(/^\d+-/, '');
                if (!groups[cleanType]) {
                  groups[cleanType] = [];
                }
                groups[cleanType].push(component);
                return groups;
              }, {} as Record<string, Component[]>);

              // Sort groups by type order (atoms, molecules, organisms, templates, pages)
              const typeOrder = ['atoms', 'molecules', 'organisms', 'templates', 'pages'];
              const sortedGroups = Object.entries(groupedComponents).sort(([a], [b]) => {
                const aIndex = typeOrder.indexOf(a);
                const bIndex = typeOrder.indexOf(b);
                return aIndex - bIndex;
              });

              // Show all type sections, even empty ones, if there are components
              const allTypeSections = typeOrder.map(type => ({
                type,
                components: groupedComponents[type] || []
              }));

              // Only show empty sections if there are components in other types
              const sectionsToShow = allTypeSections.filter(({ type, components }) => 
                components.length > 0 || Object.values(groupedComponents).some(c => c.length > 0)
              );

              return sectionsToShow.map(({ type, components }) => (
                <div key={type} className="space-y-4">
                  {/* Type Header */}
                  <div className="flex items-center space-x-3">
                    <ComponentTypeIcon type={type} size="lg" />
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white capitalize">
                      {type} ({components.length})
                    </h3>
                  </div>
                  
                  {/* Components Grid for this type */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {components.map(component => (
                      <ComponentCard key={component.name} component={component} project={project} />
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        ) : (
          /* Components in Simple Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredComponents.map(component => (
              <ComponentCard key={component.name} component={component} project={project} />
            ))}
          </div>
        )}

        {filteredComponents.length === 0 && (
          <div className="text-center py-12">
            <Search className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No components found
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              Try adjusting your search terms or filters
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetail;
